<template>
  <div class="manager-budgets">
    <div class="label">
      Manager budgets
      <small>
        Each reward is taken from the giving manager's budget. Manager budgets are created and edited in the Budgets
        tab.
      </small>
    </div>

    <div v-if="loading" class="text-center py-2"><b-spinner small variant="primary" /></div>
    <p v-else-if="!managerIds.length" class="manager-budgets__empty">
      Select who can give this award to see their budgets.
    </p>
    <template v-else>
      <ul v-if="split.withPots.length" class="manager-budgets__list">
        <li v-for="pot in split.withPots" :key="pot.recognition_budget_id" class="manager-budgets__item">
          <div class="manager-budgets__item-text">
            <span class="manager-budgets__holder">{{ pot.holder?.full_name }}</span>
            <span class="manager-budgets__meta"
              >{{ pot.name }} &middot; {{ formatBudgetPeriod(pot.budget_period) }}</span
            >
          </div>
          <div class="manager-budgets__item-usage">
            <span class="manager-budgets__left">
              {{ formatAmount(pot.remaining_minor) }} of {{ formatAmount(pot.amount_minor) }} left
            </span>
            <budget-usage-bar :budget="pot" />
          </div>
        </li>
      </ul>

      <div v-if="split.withoutPots.length" class="manager-budgets__no-pots">
        <p class="manager-budgets__no-pots-text">
          {{ noPotsText }}
          <br />
          This can be changed in budget settings.
        </p>
        <div class="manager-budgets__no-pots-names">
          <b-badge
            v-for="manager in split.withoutPots"
            :key="manager.employee_id"
            pill
            variant="light"
            class="manager-budgets__name-badge"
          >
            {{ manager.full_name }}
          </b-badge>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import BudgetUsageBar from '@/pages/recognition/components/budgets/BudgetUsageBar.vue';
import { formatBudgetPeriod, noPotsMessage, splitManagersByPot } from '@/pages/recognition/utils/budgets';
import { toCurrency } from '@/utils';
import State from '@/state/State';

const props = defineProps({
  managerIds: {
    type: Array,
    default: () => [],
  },
  pots: {
    type: Array,
    default: () => [],
  },
  employees: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const split = computed(() => splitManagersByPot(props.managerIds, props.pots, props.employees));

const enforced = computed(() =>
  Boolean(State.state?.company?.properties?.recognition_properties?.enforce_manager_budgets),
);
const noPotsText = computed(() => noPotsMessage(enforced.value));

const currency = computed(() => State.state?.company?.default_currency);
const formatAmount = minor => toCurrency(minor ?? 0, currency.value).replace(/\.00$/, '');
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';

.manager-budgets {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;

  &__empty,
  &__no-pots-text {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-30);
    margin: 0;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    border: 1px solid var(--palette-color-default-lighten-90);
    border-radius: 0.5rem;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 1rem;

    & + & {
      border-top: 1px solid var(--palette-color-default-lighten-90);
    }
  }

  &__item-text,
  &__item-usage {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  &__item-usage {
    align-items: flex-end;
    min-width: 12rem;
  }

  &__holder {
    font-weight: 500;
    font-size: 0.875rem;
  }

  &__meta,
  &__left {
    font-size: 0.8125rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__no-pots {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__no-pots-names {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
  }

  &__name-badge {
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default);
    font-weight: 500;
  }
}
</style>
